import { browser } from '$app/env'
import type { TypedSupabaseClient } from '$lib/supabase'
import type { PageDataSession } from '$lib/types'
import { updateSettingsLocally } from './session-settings-helpers.svelte'

type TimezoneInfo = {
  name: string | null
  offset: number
}

/**
 * Returns the timezone information from the browser.
 *
 * .name might be null because the postgres function will use the offset instead
 * in this case.
 */
export const getTimezoneInfo = (): TimezoneInfo => {
  if (!browser) {
    return {
      name: null,
      offset: 0,
    }
  }

  let name: string | null = null

  try {
    name = Intl.DateTimeFormat().resolvedOptions().timeZone
  } catch {
    // Ignore
  }

  return {
    name,
    offset: new Date().getTimezoneOffset(),
  }
}

/**
 * Uses the browser timezone information and converts it to a valid timezone
 * string validate with the postgresql database.
 *
 * In case of an error, this falls back to `Europe/London`.
 */
export const getBrowserTimezone = async (
  supabase: TypedSupabaseClient,
): Promise<string> => {
  if (!browser) {
    return 'Europe/London'
  }

  const timezoneInfo = getTimezoneInfo()
  const response = await supabase.rpc('get_timezone', {
    browser_timezone: timezoneInfo.name ?? 'invalid_timezone',
    browser_offset: timezoneInfo.offset,
  })

  return response.error ? 'Europe/London' : response.data
}

export const updateTimezoneForUserIfNecessary = async (
  supabase: TypedSupabaseClient,
  session: PageDataSession | null,
) => {
  try {
    if (!session || session.settings.timezone.toLowerCase() !== 'utc') return

    const timezone = await getBrowserTimezone(supabase)

    await supabase
      .from('settings')
      .update({ timezone })
      .eq('id', session.user.id)
    updateSettingsLocally({ timezone })
  } catch (e) {
    console.error('Tried updating the timezone for the user but failed', e)
  }
}
